import { useAuth, getRoleDisplayName } from '../contexts/AuthContext'
import { useTab } from '../contexts/TabContext'
import { useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect } from 'react'

export function Sidebar() {
  const { signOut, role, roles, userInfo } = useAuth()
  const { activeTab, setActiveTab } = useTab()
  const navigate = useNavigate()
  const location = useLocation()

  // Load accordion state from localStorage, default to true if not set
  const [isManagementOpen, setIsManagementOpen] = useState(() => {
    const saved = localStorage.getItem('sidebar-management-open')
    return saved !== null ? JSON.parse(saved) : true
  })

  // Save accordion state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebar-management-open', JSON.stringify(isManagementOpen))
  }, [isManagementOpen])

  // Check if user can access admin panel
  const canAccessAdminPanel = roles.includes('admin')

  // Check if current path matches
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/')

  return (
    <aside className="fixed left-0 top-0 flex w-64 flex-col border-r border-gray-200 bg-white h-screen z-10">
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="p-4 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary">
              <span className="material-symbols-outlined text-white" style={{ fontVariationSettings: "'FILL' 1" }}>
                calculate
              </span>
            </div>
            <div className="flex flex-col">
              <h1 className="text-base font-medium leading-normal text-gray-900">Project Phoenix</h1>
              <p className="text-sm font-normal leading-normal text-gray-500">Workspace</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-4 pb-4">
          <div className="flex flex-col gap-2">
            <button
              onClick={() => navigate('/books')}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isActive('/books')
                ? 'bg-primary-light text-primary'
                : 'text-gray-700 hover:bg-gray-100'
                } transition-colors w-full text-left`}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontVariationSettings: isActive('/books') ? "'FILL' 1" : "'FILL' 0" }}
              >
                book
              </span>
              <p className="text-sm font-medium leading-normal">교재 관리</p>
            </button>
            <button
              onClick={() => navigate('/hierarchy')}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isActive('/hierarchy')
                ? 'bg-primary-light text-primary'
                : 'text-gray-700 hover:bg-gray-100'
                } transition-colors w-full text-left`}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontVariationSettings: isActive('/hierarchy') ? "'FILL' 1" : "'FILL' 0" }}
              >
                account_tree
              </span>
              <p className="text-sm font-medium leading-normal">계층 구조</p>
            </button>
            <button
              onClick={() => navigate('/questions')}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isActive('/questions')
                ? 'bg-primary-light text-primary'
                : 'text-gray-700 hover:bg-gray-100'
                } transition-colors w-full text-left`}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontVariationSettings: isActive('/questions') ? "'FILL' 1" : "'FILL' 0" }}
              >
                calculate
              </span>
              <p className="text-sm font-medium leading-normal">문제 관리</p>
            </button>
            <button
              onClick={() => navigate('/solutions')}
              className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isActive('/solutions')
                ? 'bg-primary-light text-primary'
                : 'text-gray-700 hover:bg-gray-100'
                } transition-colors w-full text-left`}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontVariationSettings: isActive('/solutions') ? "'FILL' 1" : "'FILL' 0" }}
              >
                edit_note
              </span>
              <p className="text-sm font-medium leading-normal">문제 풀이</p>
            </button>
            {canAccessAdminPanel && (
              <>
                <button
                  onClick={() => setIsManagementOpen(!isManagementOpen)}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 text-gray-700 hover:bg-gray-100 transition-colors w-full text-left"
                >
                  <span
                    className="material-symbols-outlined"
                    style={{ fontVariationSettings: "'FILL' 0" }}
                  >
                    admin_panel_settings
                  </span>
                  <p className="text-sm font-medium leading-normal flex-1">관리</p>
                  <span className="material-symbols-outlined text-sm">
                    {isManagementOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                {isManagementOpen && (
                  <div className="ml-4 flex flex-col gap-2">
                    <button
                      onClick={() => navigate('/users')}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isActive('/users')
                        ? 'bg-primary-light text-primary'
                        : 'text-gray-700 hover:bg-gray-100'
                        } transition-colors w-full text-left`}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{ fontVariationSettings: isActive('/users') ? "'FILL' 1" : "'FILL' 0" }}
                      >
                        manage_accounts
                      </span>
                      <p className="text-sm font-medium leading-normal">사용자 관리</p>
                    </button>
                    <button
                      onClick={() => navigate('/tags')}
                      className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isActive('/tags')
                        ? 'bg-primary-light text-primary'
                        : 'text-gray-700 hover:bg-gray-100'
                        } transition-colors w-full text-left`}
                    >
                      <span
                        className="material-symbols-outlined"
                        style={{ fontVariationSettings: isActive('/tags') ? "'FILL' 1" : "'FILL' 0" }}
                      >
                        label
                      </span>
                      <p className="text-sm font-medium leading-normal">태그</p>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </nav>
        <div className="p-4 flex flex-col gap-2 flex-shrink-0">
          {/* User Role Badge */}
          {userInfo && (
            <div className="px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
              <div className="flex items-center gap-2 mb-1">
                <span className="material-symbols-outlined text-gray-500 text-sm">account_circle</span>
                <p className="text-xs font-medium text-gray-500">현재 사용자</p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-900 truncate">{userInfo.name}</p>
                <span
                  className={`text-xs font-bold px-2 py-0.5 rounded-full ${role === 'admin'
                    ? 'bg-purple-100 text-purple-700'
                    : role === 'moderator'
                      ? 'bg-indigo-100 text-indigo-700'
                      : role === 'user'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-900 text-gray-100'
                    }`}
                >
                  {getRoleDisplayName(role)}
                </span>
              </div>
            </div>
          )}
          <button
            onClick={() => navigate('/settings')}
            className={`flex items-center gap-3 rounded-lg px-3 py-2 ${isActive('/settings')
              ? 'bg-primary-light text-primary'
              : 'text-gray-700 hover:bg-gray-100'
              } transition-colors w-full text-left`}
          >
            <span
              className="material-symbols-outlined"
              style={{ fontVariationSettings: isActive('/settings') ? "'FILL' 1" : "'FILL' 0" }}
            >
              settings
            </span>
            <p className="text-sm font-medium leading-normal">설정</p>
          </button>
          <button
            onClick={() => signOut()}
            className="flex items-center gap-3 rounded-lg px-3 py-2 text-gray-700 hover:bg-gray-100 transition-colors w-full text-left"
          >
            <span className="material-symbols-outlined">logout</span>
            <p className="text-sm font-medium leading-normal">로그아웃</p>
          </button>
        </div>
      </div>
    </aside>
  )
}
